import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { formatDate, humanLogs } from '~/components/tasks/taskStyle';
import type { TaskDetail } from '~/data/generated/model';
import { useTaskKnowledge } from '~/data/hooks/useTaskKnowledge';
import { BUTTON_SX, SURFACE_RADIUS } from '~/theme';

export type PublishMode = 'publish' | 'update' | 'complete';

export function taskSummaryText(task: TaskDetail): string {
  return (
    task.summary ||
    humanLogs(task)
      .reverse()
      .map((l) => l.text)
      .join(' ')
  );
}

type Props = {
  task: TaskDetail;
  onPublish: (mode: PublishMode) => void;
  // Left out where there is no Knowledge tab to go to.
  onViewKnowledge?: () => void;
  autoGenerate?: boolean;
};

export function AiSummaryCard({ task, onPublish, onViewKnowledge, autoGenerate = true }: Props) {
  const knowledgeEnabled = useKnowledgeEnabled();
  const { summarize } = useTaskKnowledge(task.id);
  const requestedFor = useRef<number | null>(null);
  const [failedFor, setFailedFor] = useState<number | null>(null);
  const logCount = humanLogs(task).length;
  const summarizing = autoGenerate && task.summaryLogCount < logCount && failedFor !== logCount;

  useEffect(() => {
    if (!summarizing || requestedFor.current === logCount) return;
    requestedFor.current = logCount;
    summarize().catch(() => setFailedFor(logCount));
  }, [summarizing, logCount, summarize]);

  const published = Boolean(task.publishedAt);
  const newLogs = task.substantiveLogCount - task.publishedLogCount;
  const stale = published && newLogs > 0;
  const entries = task.publishedEntryCount;

  let buttonLabel = 'Publish to knowledge now';
  if (stale) buttonLabel = 'Review update';
  else if (published) buttonLabel = 'Publish more';

  return (
    <Box
      component="section"
      aria-label="AI summary"
      sx={{
        bgcolor: '#f7f9fd',
        border: '1px solid #dde5f2',
        borderRadius: `${SURFACE_RADIUS}px`,
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: '#1a3f8f' }} />
        <Box
          component="span"
          sx={{ fontSize: 12, fontWeight: 500, letterSpacing: '.06em', color: '#555', flex: 1 }}
        >
          AI SUMMARY
        </Box>
        <Box component="span" sx={{ fontSize: 12, color: '#666' }}>
          Updated after log {task.summaryLogCount || logCount}
        </Box>
      </Box>
      <Box sx={{ fontSize: 14, lineHeight: 1.55, color: '#1b1f24', textWrap: 'pretty' }}>
        {summarizing ? 'Updating summary…' : taskSummaryText(task)}
      </Box>
      <Box sx={{ fontSize: 12, color: '#666' }}>
        Rewritten from the whole task each time a log is added, so outdated facts drop out.
      </Box>
      {knowledgeEnabled && stale ? (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            p: '10px 12px',
            borderRadius: '8px',
            bgcolor: '#fff7e8',
            border: '1px solid #f0cf8f',
            fontSize: 13,
            color: '#5c3d00',
          }}
        >
          <UpdateIcon sx={{ fontSize: 18, color: '#8a5a00' }} />
          {newLogs} new log{newLogs === 1 ? '' : 's'} since this was published. Knowledge may be out
          of date.
        </Box>
      ) : null}
      {knowledgeEnabled && task.publishedAt ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 13, color: '#1e6b4f' }}
        >
          <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />
          Published to knowledge · {formatDate(task.publishedAt)} · {entries}{' '}
          {entries === 1 ? 'entry' : 'entries'}
          {onViewKnowledge ? (
            <Button
              size="small"
              onClick={onViewKnowledge}
              sx={{ ...BUTTON_SX, minWidth: 0, py: 0 }}
            >
              View
            </Button>
          ) : null}
        </Box>
      ) : null}
      {knowledgeEnabled ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px',
            borderTop: '1px solid #dde5f2',
            pt: '12px',
          }}
        >
          <Button
            variant={stale ? 'contained' : 'outlined'}
            startIcon={<BookmarkAddOutlinedIcon />}
            onClick={() => onPublish(published ? 'update' : 'publish')}
            sx={{ ...BUTTON_SX, ...(stale ? {} : { bgcolor: '#fff' }) }}
          >
            {buttonLabel}
          </Button>
          <Box component="span" sx={{ fontSize: 12, color: '#666' }}>
            You review every entry before it&apos;s saved
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
